import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { userApi } from '@/services/api';
import { useTimezone } from './TimezoneContext';
import { detectTimezone } from '@/utils/timezone';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isGuestMode: boolean;
  guestId: string | null;
  signInWithEmail: (email: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  enterGuestMode: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Guest ID management functions
const generateGuestId = (): string => `guest-${crypto.randomUUID()}`;
const getStoredGuestId = (): string | null => localStorage.getItem('guestId');
const storeGuestId = (id: string): void => localStorage.setItem('guestId', id);
const clearGuestId = (): void => localStorage.removeItem('guestId');

export function AuthProvider({ children }: { children: ReactNode }) {
  const { updateTimezone } = useTimezone();
  
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [guestId, setGuestId] = useState<string | null>(null);

  // Handle auth state changes
  const handleAuthChange = async (newSession: Session | null) => {
    if (newSession) {
      // User authenticated - exit guest mode
      setIsGuestMode(false);
      setGuestId(null);
      clearGuestId();
      setSession(newSession);
      setUser(newSession?.user ?? null);

      // Sync timezone when user authenticates
      try {
        const timezone = detectTimezone();
        updateTimezone(timezone);
        await userApi.updateTimezone(timezone);
      } catch (error) {
        console.warn('Failed to sync timezone:', error);
      }
    } else {
      setSession(null);
      setUser(null);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    // Check for existing guest mode first
    const storedGuestId = getStoredGuestId();
    if (storedGuestId && mounted) {
      setIsGuestMode(true);
      setGuestId(storedGuestId);
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        handleAuthChange(session);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (mounted) {
          handleAuthChange(session);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [updateTimezone]); // Include updateTimezone in deps

  const signInWithEmail = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const signOut = async () => {
    if (isGuestMode) {
      // Clear guest mode
      setIsGuestMode(false);
      setGuestId(null);
      clearGuestId();
    } else {
      await supabase.auth.signOut();
    }
  };

  const enterGuestMode = () => {
    const newGuestId = generateGuestId();
    setIsGuestMode(true);
    setGuestId(newGuestId);
    storeGuestId(newGuestId);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      isGuestMode,
      guestId,
      signInWithEmail,
      signOut,
      enterGuestMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}